import { useApp } from '../store';
import {
  MATERIAL_LIST, MATERIALS, nearestGauge, getKFactor,
  STANDARD_V_DIES, radiusFromVDie, vDieFromRadius,
} from '../lib/materials';
import { EdgeKey, Hole, DimMode } from '../lib/types';
import {
  PresetKind, flangeLengthDisplay, flangeLengthFromDisplay,
  flangeWidthDisplay, flangeWidthFromDisplay,
} from '../lib/part';
import { unfold, estimatedMass, flatArea } from '../lib/unfold';
import { validate } from '../lib/validate';
import { useMemo, useState } from 'react';

const DIM_MODES: { key: DimMode; label: string; hint: string }[] = [
  { key: 'tangent', label: 'Tangent', hint: 'Bend tangent → far edge' },
  { key: 'outside', label: 'Outside', hint: 'Outside-to-outside' },
  { key: 'inside',  label: 'Inside',  hint: 'Inside-to-inside' },
];

const EDGES: { key: EdgeKey; label: string }[] = [
  { key: '+x', label: '+X (right)' },
  { key: '-x', label: '−X (left)' },
  { key: '+y', label: '+Y (front)' },
  { key: '-y', label: '−Y (back)' },
];

const PRESETS: { key: PresetKind; label: string; hint: string }[] = [
  { key: 'single', label: 'Single', hint: '1 flange · 90° bend' },
  { key: 'zbend', label: 'Z-bend', hint: '2 bends · offset step' },
  { key: 'joggle', label: 'Joggle', hint: '2× 30° · small step' },
  { key: 'hem', label: 'Hem', hint: '175° fold-back' },
];

function NumField({
  label, value, step = 0.5, min, max, onChange, suffix,
}: {
  label: string; value: number; step?: number; min?: number; max?: number;
  onChange: (v: number) => void; suffix?: string;
}) {
  // While the user is typing we hold the raw string locally so transient values
  // (empty, "0.", "5" before the rest of "50") aren't clamped/snapped back by
  // the store and don't yank the cursor. We sync to the store's value only
  // when the input is NOT focused so external updates (mode toggle, undo, etc.)
  // still come through cleanly.
  const [local, setLocal] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const displayedValue = Number.isFinite(value) ? value : 0;
  const shown = editing ? local : String(displayedValue);

  return (
    <div className="field">
      <label>{label}{suffix ? <span className="text-mute/60 ml-1">{suffix}</span> : null}</label>
      <input
        type="number"
        inputMode="decimal"
        value={shown}
        step={step}
        min={min}
        max={max}
        onFocus={() => { setLocal(String(displayedValue)); setEditing(true); }}
        onChange={(e) => {
          setLocal(e.target.value);
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
    </div>
  );
}

export default function Sidebar() {
  const part = useApp((s) => s.part);
  const selectedId = useApp((s) => s.selectedId);
  const select = useApp((s) => s.select);
  const setMat = useApp((s) => s.setMat);
  const setThickness = useApp((s) => s.setThickness);
  const setMiterGap = useApp((s) => s.setMiterGap);
  const setDimMode = useApp((s) => s.setDimMode);
  const addHoleAction = useApp((s) => s.addHole);
  const removeHole = useApp((s) => s.removeHole);
  const patchHole = useApp((s) => s.patchHole);
  const addChild = useApp((s) => s.addChild);
  const doApplyPreset = useApp((s) => s.applyPreset);
  const remove = useApp((s) => s.remove);
  const patchFlange = useApp((s) => s.patchFlange);
  const patchAttachment = useApp((s) => s.patchAttachment);
  const reset = useApp((s) => s.reset);

  const [preset, setPreset] = useState<PresetKind>('single');

  const sel = selectedId ? part.flanges[selectedId] : null;
  const flat = useMemo(() => unfold(part), [part]);
  const issues = useMemo(() => validate(part), [part]);
  const mat = MATERIALS[part.material];
  const gauge = useMemo(() => nearestGauge(part.material, part.thickness), [part.material, part.thickness]);
  const mass = useMemo(() => estimatedMass(flat, part.thickness, mat.density), [flat, part.thickness, mat.density]);
  const area = useMemo(() => flatArea(flat), [flat]);

  const errors = issues.filter((i) => i.severity === 'error');
  const warns = issues.filter((i) => i.severity === 'warn');

  return (
    <aside
      className={
        // Desktop (lg+ = 1024px+): right-docked sidebar, full height.
        // Mobile / tablet portrait (< lg): bottom-docked panel taking ~45vh.
        // The lg break (not md) keeps phones in landscape — which can be up
        // to ~932px wide on Pro Max — in the stacked layout the user wants.
        'bg-panel border-line flex flex-col text-sm overflow-hidden ' +
        'shrink-0 w-full lg:w-80 ' +
        'h-[45vh] lg:h-auto ' +
        'border-t lg:border-t-0 lg:border-l'
      }
    >
      <div className="px-4 py-3 border-b border-line">
        <div className="text-xs uppercase tracking-wider text-mute">Material</div>
        <select
          value={part.material}
          onChange={(e) => setMat(e.target.value as any)}
          className="w-full bg-panel2 border border-line rounded px-2 py-1.5 mt-2 text-ink"
        >
          {MATERIAL_LIST.map((m) => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
        <div className="space-y-1.5 mt-3">
          <div className="field">
            <label>Thickness <span className="text-mute/60 ml-1">mm</span></label>
            <select
              value={String(gauge.thickness)}
              onChange={(e) => setThickness(parseFloat(e.target.value))}
              className="bg-panel2 border border-line rounded px-2 py-1 text-ink w-28 text-right"
            >
              {mat.gauges.map((g) => (
                <option key={g.thickness} value={String(g.thickness)}>{g.thickness.toFixed(1)}</option>
              ))}
            </select>
          </div>
          <NumField label="Miter gap" suffix="mm" value={part.miterGap} step={0.05} min={0}
            onChange={(v) => setMiterGap(v)} />
          <div className="field">
            <label>Dimensions</label>
            <div className="flex bg-bg border border-line rounded p-0.5">
              {DIM_MODES.map((dm) => (
                <button
                  key={dm.key}
                  title={dm.hint}
                  onClick={() => setDimMode(dm.key)}
                  className={
                    'px-2 py-0.5 rounded text-[11px] ' +
                    (part.dimMode === dm.key
                      ? 'bg-accent text-black'
                      : 'text-mute hover:text-ink')
                  }
                >
                  {dm.label}
                </button>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-mute pt-1 leading-relaxed">
            Default for {part.thickness.toFixed(1)} mm {mat.label}:
            K={getKFactor(part.material, gauge.defaultBendRadius, part.thickness).toFixed(2)},
            R={gauge.defaultBendRadius.toFixed(1)} mm, min flange {gauge.minFlangeLength.toFixed(1)} mm.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        <div>
          <div className="section-title">Tree</div>
          <FlangeTree />
        </div>

        {sel && (
          <div className="section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-mute">Selected: {sel.name}</div>
              {sel.id !== part.rootId && (
                <button className="btn-ghost text-red-400" onClick={() => remove(sel.id)}>Delete</button>
              )}
            </div>
            <div className="space-y-1.5">
              <NumField
                label={part.dimMode === 'tangent' ? 'Length' : `Length (${part.dimMode})`}
                suffix="mm"
                value={flangeLengthDisplay(part, sel)}
                onChange={(v) => patchFlange(sel.id, { length: Math.max(1, flangeLengthFromDisplay(part, sel, v)) })}
              />
              {part.dimMode !== 'tangent' && flangeLengthDisplay(part, sel) !== sel.length && (
                <div className="text-[10px] text-mute leading-tight pl-2 -mt-1">
                  tangent: {sel.length.toFixed(2)} mm
                </div>
              )}
              <NumField
                label={part.dimMode === 'tangent' ? 'Width' : `Width (${part.dimMode})`}
                suffix="mm"
                value={flangeWidthDisplay(part, sel)}
                onChange={(v) => patchFlange(sel.id, { width: Math.max(1, flangeWidthFromDisplay(part, sel, v)) })}
              />
              {part.dimMode !== 'tangent' && flangeWidthDisplay(part, sel) !== sel.width && (
                <div className="text-[10px] text-mute leading-tight pl-2 -mt-1">
                  tangent: {sel.width.toFixed(2)} mm
                </div>
              )}
            </div>
            {sel.attachment && (
              <div className="space-y-1.5 mt-2">
                <div className="field">
                  <label>Attached edge</label>
                  <select
                    value={sel.attachment.edge}
                    onChange={(e) => patchAttachment(sel.id, { edge: e.target.value as EdgeKey })}
                    className="bg-panel2 border border-line rounded px-2 py-1 text-ink w-28"
                  >
                    {EDGES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                  </select>
                </div>
                <NumField label="Offset" suffix="mm" value={sel.attachment.offset}
                  onChange={(v) => patchAttachment(sel.id, { offset: Math.max(0, v) })} />
                <NumField label="Bend angle" suffix="°" value={sel.attachment.bendAngle} step={1} min={-180} max={180}
                  onChange={(v) => patchAttachment(sel.id, { bendAngle: Math.max(-180, Math.min(180, v)) })} />
                <NumField label="Bend radius" suffix="mm" value={sel.attachment.bendRadius} step={0.1} min={0.1}
                  onChange={(v) => patchAttachment(sel.id, { bendRadius: Math.max(0.1, v) })} />
                <div className="field">
                  <label>V-die <span className="text-mute/60 ml-1">mm</span></label>
                  <select
                    value={String(vDieFromRadius(part.material, sel.attachment.bendRadius))}
                    onChange={(e) => patchAttachment(sel.id, {
                      bendRadius: radiusFromVDie(part.material, parseFloat(e.target.value)),
                    })}
                    className="bg-panel2 border border-line rounded px-2 py-1 text-ink w-28 text-right"
                  >
                    {STANDARD_V_DIES.map((v) => (
                      <option key={v} value={String(v)}>V{v}</option>
                    ))}
                  </select>
                </div>
                <div className="text-[10px] text-mute leading-tight pl-2 -mt-1">
                  K (auto): <span className="text-ink">{getKFactor(part.material, sel.attachment.bendRadius, part.thickness).toFixed(2)}</span>
                  {' · '}R/T: {(sel.attachment.bendRadius / part.thickness).toFixed(2)}
                  {' · '}≈ V{vDieFromRadius(part.material, sel.attachment.bendRadius)} (16% rule)
                </div>
                <label className="field cursor-pointer">
                  <span>Miter at corner</span>
                  <input
                    type="checkbox"
                    checked={!!sel.attachment.miterCorner}
                    onChange={(e) => patchAttachment(sel.id, { miterCorner: e.target.checked })}
                    className="w-4 h-4 mr-24 accent-accent"
                  />
                </label>
                <div className="text-[10px] text-mute leading-tight pl-1">
                  45° cut at the corner shared with a perpendicular sibling. Both flanges must
                  enable this.
                </div>
              </div>
            )}

            <div className="section">
              <div className="text-xs uppercase tracking-wider text-mute mb-2">Add flange</div>
              <div className="grid grid-cols-2 gap-1 mb-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setPreset(p.key)}
                    className={
                      'px-2 py-1 rounded text-xs border transition-colors text-left ' +
                      (preset === p.key
                        ? 'border-accent bg-accent/15 text-accent'
                        : 'border-line bg-panel2 text-mute hover:text-ink')
                    }
                  >
                    <div className="font-medium leading-tight">{p.label}</div>
                    <div className="text-[10px] opacity-75 leading-tight">{p.hint}</div>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {EDGES.map((e) => (
                  <button
                    key={e.key}
                    className="btn"
                    onClick={() =>
                      preset === 'single'
                        ? addChild(sel.id, e.key, {})
                        : doApplyPreset(sel.id, e.key, preset)
                    }
                  >
                    {e.label}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-mute mt-2 leading-snug">
                {preset === 'single' && `90° bend at R=${gauge.defaultBendRadius.toFixed(1)} mm.`}
                {preset === 'zbend' && 'Two perpendicular bends (+90° then −90°) form an offset step.'}
                {preset === 'joggle' && 'Two ±30° bends form a small overlap step.'}
                {preset === 'hem' && '175° fold-back, R=' + Math.max(part.thickness * 0.5, 0.5).toFixed(2) + ' mm. Note: exceeds 130° standard limit.'}
              </div>
            </div>

            <div className="section">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-mute">
                  Holes ({part.holes.filter((h) => h.flangeId === sel.id).length})
                </div>
                <button
                  className="btn-ghost text-accent"
                  onClick={() => addHoleAction(sel.id)}
                  title="Add a hole at the center of the flange"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {part.holes
                  .filter((h) => h.flangeId === sel.id)
                  .map((h) => (
                    <HoleEditor
                      key={h.id}
                      hole={h}
                      onPatch={(p) => patchHole(h.id, p)}
                      onDelete={() => removeHole(h.id)}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <div className="section-title">Flat blank</div>
          <div className="text-mute text-xs leading-relaxed space-y-0.5">
            <div>Bbox <span className="text-ink">{flat.bbox.w.toFixed(1)} × {flat.bbox.h.toFixed(1)} mm</span></div>
            <div>Area <span className="text-ink">{(area / 100).toFixed(1)} cm²</span></div>
            <div>Mass <span className="text-ink">{mass < 0.1 ? `${(mass * 1000).toFixed(0)} g` : `${mass.toFixed(2)} kg`}</span></div>
            <div>{Object.keys(part.flanges).length} flange{Object.keys(part.flanges).length === 1 ? '' : 's'}</div>
          </div>
        </div>

        {(errors.length > 0 || warns.length > 0) && (
          <div className="section">
            <div className="section-title">Issues</div>
            <ul className="space-y-1 text-xs">
              {errors.map((i, k) => (
                <li key={`e${k}`} className="text-red-400 leading-snug cursor-pointer hover:underline"
                    onClick={() => select(i.flangeId)}>
                  ✕ {i.message}
                </li>
              ))}
              {warns.map((i, k) => (
                <li key={`w${k}`} className="text-yellow-400/80 leading-snug cursor-pointer hover:underline"
                    onClick={() => select(i.flangeId)}>
                  ⚠ {i.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="section">
          <button className="btn w-full" onClick={() => { if (confirm('Reset to default?')) reset(); select(null); }}>
            Reset part
          </button>
        </div>
      </div>
    </aside>
  );
}

function FlangeTree() {
  const part = useApp((s) => s.part);
  const selectedId = useApp((s) => s.selectedId);
  const select = useApp((s) => s.select);

  const renderNode = (id: string, depth: number) => {
    const f = part.flanges[id];
    if (!f) return null;
    return (
      <div key={id}>
        <button
          onClick={() => select(id)}
          className={
            'w-full text-left px-2 py-1 rounded text-xs ' +
            (id === selectedId ? 'bg-accent/15 text-accent' : 'text-ink hover:bg-panel2')
          }
          style={{ paddingLeft: 8 + depth * 12 }}
        >
          {f.name}
          <span className="text-mute ml-2">{f.length.toFixed(0)}×{f.width.toFixed(0)}</span>
        </button>
        {f.children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return <div className="space-y-0.5">{renderNode(part.rootId, 0)}</div>;
}

function HoleEditor({
  hole,
  onPatch,
  onDelete,
}: {
  hole: Hole;
  onPatch: (patch: Partial<Hole>) => void;
  onDelete: () => void;
}) {
  return (
    <div className="bg-panel2 border border-line rounded p-2 space-y-1.5">
      <div className="flex items-center justify-between">
        <select
          value={hole.kind}
          onChange={(e) => onPatch({ kind: e.target.value as Hole['kind'] })}
          className="bg-bg border border-line rounded px-1.5 py-0.5 text-xs text-ink"
        >
          <option value="round">Round</option>
          <option value="slot">Slot</option>
        </select>
        <button className="btn-ghost text-red-400 text-xs" onClick={onDelete}>×</button>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <NumField label="X" value={hole.x} step={0.5} onChange={(v) => onPatch({ x: v })} />
        <NumField label="Y" value={hole.y} step={0.5} onChange={(v) => onPatch({ y: v })} />
        <NumField label="Ø" value={hole.diameter} step={0.5} min={0.5}
          onChange={(v) => onPatch({ diameter: Math.max(0.5, v) })} />
        {hole.kind === 'slot' && (
          <NumField label="Len" value={hole.length ?? hole.diameter} step={0.5}
            min={hole.diameter}
            onChange={(v) => onPatch({ length: Math.max(hole.diameter, v) })} />
        )}
        {hole.kind === 'slot' && (
          <NumField label="Rot°" value={hole.rotation ?? 0} step={5} min={-180} max={180}
            onChange={(v) => onPatch({ rotation: v })} />
        )}
      </div>
    </div>
  );
}
